/**
 * PendingApprovals — wrapper around RequestList.
 *
 * Requests awaiting the current user's (or any approver's) action.
 * Full action set: approve / reject / delegate.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import RequestList, { RequestSummaryCard } from '../components/RequestList';
import { ACTIONABLE_REQUEST_STATUSES } from '../../../api/processes';
import type { ProcessRequest } from '../../../api/processes';

const PendingApprovals: React.FC = () => {
  const { t } = useTranslation();
  const buildSummary = (rows: ProcessRequest[]): RequestSummaryCard[] => {
    const total = rows.length;
    const urgent = rows.filter(
      (r) => r.priority === 'critical' || r.priority === 'urgent' || r.priority === 'high',
    ).length;
    const now = Date.now();
    const over24h = rows.filter((r) => {
      const ts = r.submitted_at ?? r.created_at;
      return ts && now - Date.parse(ts) > 24 * 3_600_000;
    }).length;
    const overdue = rows.filter(
      (r) => r.due_date && Date.parse(r.due_date) < now,
    ).length;
    return [
      { id: 'total', title: t('processes:requests.summary.pendingApproval'), value: String(total), color: 'warning' },
      { id: 'urgent', title: t('processes:requests.summary.urgent'), value: String(urgent), color: 'error' },
      { id: 'over24h', title: t('processes:requests.summary.over24h'), value: String(over24h), color: 'warning', description: t('processes:requests.summary.over24hDesc') },
      { id: 'overdue', title: t('processes:requests.summary.pastDue'), value: String(overdue), color: 'error' },
    ];
  };
  return (
    <RequestList
      title={t('processes:requests.wrappers.pendingApprovals')}
      description={t('processes:requests.wrappers.pendingApprovalsDesc')}
      statuses={ACTIONABLE_REQUEST_STATUSES}
      approverOnly
      allowedActions={['approve', 'reject', 'delegate']}
      buildSummary={buildSummary}
    />
  );
};

export default PendingApprovals;
